import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../config/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const checkUser = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          setIsLoggedIn(true);
        }
      } catch (error) {
        console.error('Error checking user:', error);
      } finally {
        setLoading(false);
      }
    };

    checkUser();

    // Listen to auth state changes
    const { data: { subscription } } = authService.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        setUser(session.user);
    setIsLoggedIn(true);
        
        // Ensure customer record exists for signed in user
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // Use the ensureCustomerRecord function
          ensureCustomerRecord(session.user);
        }
      } else {
        setUser(null);
        setIsLoggedIn(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email, password, userData = {}) => {
    try {
      console.log('Starting signup process for:', email);
      const signupResult = await authService.signUp(email, password, userData);
      console.log('Signup result:', signupResult);
      
      // Create customer record immediately (no email verification required)
      if (signupResult.user) {
        try {
          console.log('Creating customer record for user:', signupResult.user.email);
          const { dataService } = await import('../services/dataService');
          const { countryService } = await import('../services/countryService');
          const customerData = {
            auth_uid: signupResult.user.id, // Use auth UID
            name: userData.name || signupResult.user.user_metadata?.name || email.split('@')[0],
            email: email,
            phone: userData.phone || signupResult.user.user_metadata?.phone || null,
            address: userData.address || signupResult.user.user_metadata?.address || null,
            city: userData.city || signupResult.user.user_metadata?.city || null,
            country: userData.country || signupResult.user.user_metadata?.country || null,
            currency: userData.currency || signupResult.user.user_metadata?.currency || null,
            postal_code: userData.postal_code || signupResult.user.user_metadata?.postal_code || null
          };
          
          console.log('Customer data to create:', customerData);
          const newCustomer = await dataService.createCustomer(customerData);
          console.log('Customer record created successfully:', newCustomer);
          
          // Save country to countryService if available
          if (customerData.country) {
            await countryService.setSelectedCountry(customerData.country);
          }
        } catch (dbError) {
          console.error('Error creating customer record during signup:', dbError);
          // Don't fail signup if customer creation fails
        }
      } else {
        console.log('No user in signup result');
      }
      
      return { success: true, user: signupResult.user, session: signupResult.session };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: error.message };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { user: signedInUser } = await authService.signIn(email, password);
      
      // Ensure customer record exists in database
      if (signedInUser) {
        try {
          const { dataService } = await import('../services/dataService');
          const { countryService } = await import('../services/countryService');
          const existingCustomer = await dataService.getCustomerByEmail(email);
          
          if (!existingCustomer) {
            // Create customer record if it doesn't exist
            const customerData = {
              auth_uid: signedInUser.id, // Use auth UID
              name: signedInUser.user_metadata?.name || email.split('@')[0],
              email: email,
              phone: signedInUser.user_metadata?.phone || null,
              address: signedInUser.user_metadata?.address || null,
              city: signedInUser.user_metadata?.city || null,
              country: signedInUser.user_metadata?.country || null,
              currency: signedInUser.user_metadata?.currency || null,
              postal_code: signedInUser.user_metadata?.postal_code || null
            };
            await dataService.createCustomer(customerData);
            console.log('Customer record created during sign in');
            
            // Save country to countryService if available
            if (customerData.country) {
              await countryService.setSelectedCountry(customerData.country);
            }
          } else {
            // If customer exists, update countryService with their country
            if (existingCustomer.country) {
              await countryService.setSelectedCountry(existingCustomer.country);
            }
          }
        } catch (dbError) {
          console.error('Error handling customer record during sign in:', dbError);
          // Don't fail signin if customer creation fails
        }
      }
      
      return { success: true, user: signedInUser };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const signOut = async () => {
    try {
      await authService.signOut();
    setUser(null);
    setIsLoggedIn(false);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const resetPassword = async (email) => {
    try {
      await authService.resetPassword(email);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const ensureCustomerRecord = async (user) => {
    if (!user?.email) {
      console.log('No user email provided for customer record creation');
      return;
    }
    
    try {
      console.log('Ensuring customer record for user:', user.email);
      const { dataService } = await import('../services/dataService');
      
      // Check if customer already exists (prefer auth UID)
      let existingCustomer = await dataService.getCustomerByAuthUid(user.id);
      if (!existingCustomer && user?.email) {
        existingCustomer = await dataService.getCustomerByEmail(user.email);
      }
      
      if (existingCustomer) {
        console.log('Customer record already exists:', existingCustomer);
        return existingCustomer;
      }
      
      // Create new customer record
      const customerData = {
        auth_uid: user.id, // Use auth UID
        name: user.user_metadata?.name || user.email.split('@')[0],
        email: user.email,
        phone: user.user_metadata?.phone || null,
        address: user.user_metadata?.address || null,
        city: user.user_metadata?.city || null,
        country: user.user_metadata?.country || null,
        currency: user.user_metadata?.currency || null,
        postal_code: user.user_metadata?.postal_code || null
      };
      
      console.log('Creating customer record with data:', customerData);
      const newCustomer = await dataService.createCustomer(customerData);
      console.log('Customer record created successfully:', newCustomer);
      return newCustomer;
      
    } catch (dbError) {
      console.error('Error ensuring customer record:', dbError);
      console.error('Error details:', {
        message: dbError.message,
        code: dbError.code,
        details: dbError.details
      });
    }
  };

  const updateUserProfile = async (profileData) => {
    try {
      if (!user?.id) {
        throw new Error('No authenticated user found');
      }

      console.log('Updating user profile with data:', profileData);

      // Update Supabase Auth user metadata
      const { data: updatedUser, error: authError } = await authService.updateUser({
        data: {
          name: profileData.name,
          phone: profileData.phone,
          date_of_birth: profileData.date_of_birth,
          address: profileData.address,
          city: profileData.city,
          country: profileData.country,
          currency: profileData.currency,
          postal_code: profileData.postal_code,
          preferences: profileData.preferences
        }
      });

      if (authError) {
        throw authError;
      }

      // Update customer record in database
      const { dataService } = await import('../services/dataService');
      const customerData = {
        name: profileData.name,
        phone: profileData.phone,
        address: profileData.address,
        city: profileData.city,
        country: profileData.country,
        currency: profileData.currency,
        postal_code: profileData.postal_code,
        preferences: profileData.preferences
      };

      await dataService.updateCustomerByEmail(user.email, customerData);

      // Update local user state
      setUser(updatedUser.user);

      console.log('Profile updated successfully');
      return { success: true, user: updatedUser.user };

    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    isLoggedIn,
    user,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    ensureCustomerRecord,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
